import { LitElement, html, css } from 'lit'
import { StoreMixin } from '../store.js'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'
import './rich-editor.js'

// Knowledge Base browse + search (§11). Folders (public or client-scoped) expand to
// their articles; search is permission-scoped server-side. Agents can author.
class PageKb extends StoreMixin(LitElement) {
  static properties = {
    _folders: { state: true },
    _loading: { state: true },
    _query: { state: true },
    _results: { state: true },
    _articlesByFolder: { state: true },
    _expanded: { state: true },
    _clients: { state: true },
    _showFolderForm: { state: true },
    _showArticleForm: { state: true },
    _fName: { state: true },
    _fClient: { state: true },
    _fParent: { state: true },
    _aFolder: { state: true },
    _aTitle: { state: true },
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: 0.75rem; flex-wrap: wrap; }
    h1 { color: var(--bx-brand); margin: 0; font-size: 1.4rem; flex: 1; }
    .search { width: 100%; box-sizing: border-box; font: inherit; padding: 0.6rem 0.8rem; margin-bottom: 1rem;
              border: 1px solid var(--bx-border); border-radius: var(--bx-radius); background: var(--bx-bg); color: var(--bx-ink); }
    .card { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); overflow: hidden; margin-bottom: 1rem; }
    .folder { display: flex; align-items: center; gap: 0.5rem; padding: 0.7rem 1rem;
              border-bottom: 1px solid var(--bx-border); }
    .folder:hover { background: var(--bx-bg); }
    .folder .label { flex: 1; cursor: pointer; }
    .caret { color: var(--bx-muted); font-size: 0.7rem; width: 0.8rem; display: inline-block; }
    .fname { font-weight: 600; color: var(--bx-ink); }
    .del { font: inherit; font-size: 0.78rem; cursor: pointer; padding: 0.15rem 0.5rem; border: 1px solid #f3c4c4;
           border-radius: var(--bx-radius); background: var(--bx-surface); color: #b42318; }
    .badge { font-size: 0.7rem; padding: 0.1rem 0.5rem; border-radius: 999px; margin-left: 0.5rem; vertical-align: middle; }
    .badge.pub { background: #e6f4ea; color: #1e7e34; }
    .badge.priv { background: #fdeaea; color: #a61b1b; }
    .count { color: var(--bx-muted); font-size: 0.82rem; }
    .articles { padding: 0.25rem 1rem 0.6rem 1.5rem; }
    .articles a { display: block; padding: 0.3rem 0; color: var(--bx-brand); cursor: pointer; text-decoration: none; font-size: 0.92rem; }
    .articles a:hover { text-decoration: underline; }
    .none { color: var(--bx-muted); font-size: 0.85rem; padding: 0.3rem 0; }
    .result { padding: 0.7rem 1rem; border-bottom: 1px solid var(--bx-border); cursor: pointer; }
    .result:last-child { border-bottom: none; }
    .result:hover { background: var(--bx-bg); }
    .result .t { font-weight: 600; color: var(--bx-ink); }
    .result .f { color: var(--bx-muted); font-size: 0.8rem; }
    .result .s { color: var(--bx-muted); font-size: 0.85rem; margin-top: 0.15rem; }
    .result .s b { color: var(--bx-ink); }
    .toolbar { display: flex; gap: 0.5rem; }
    button { font: inherit; cursor: pointer; padding: 0.4rem 0.8rem; border: 1px solid var(--bx-border);
             border-radius: var(--bx-radius); background: var(--bx-surface); color: var(--bx-ink); font-size: 0.85rem; }
    button.primary { background: var(--bx-accent); color: var(--bx-accent-ink); border-color: transparent; font-weight: 700; }
    .form { background: var(--bx-surface); border: 1px solid var(--bx-border); border-radius: var(--bx-radius);
            box-shadow: var(--bx-shadow); padding: 1rem; margin-bottom: 1rem; }
    .form input, .form select, .form textarea { width: 100%; box-sizing: border-box; font: inherit; padding: 0.5rem;
             border: 1px solid var(--bx-border); border-radius: var(--bx-radius); margin-bottom: 0.6rem; background: var(--bx-bg); color: var(--bx-ink); }
    .form textarea { min-height: 220px; resize: vertical; font-family: ui-monospace, monospace; font-size: 0.9rem; }
    .hint { color: var(--bx-muted); font-size: 0.8rem; margin: 0 0 0.6rem; }
    .empty, .loading { padding: 3rem; text-align: center; color: var(--bx-muted); }
  `

  constructor() {
    super()
    this._folders = []; this._loading = true
    this._query = ''; this._results = null
    this._articlesByFolder = {}; this._expanded = new Set()
    this._clients = []
    this._showFolderForm = false; this._showArticleForm = false
    this._fName = ''; this._fClient = ''; this._fParent = ''
    this._aFolder = ''; this._aTitle = ''
    this._busy = false
  }

  connectedCallback() { super.connectedCallback(); this._load() }
  disconnectedCallback() { super.disconnectedCallback(); clearTimeout(this._searchTimer) }
  get _isAgent() { return this.appState.user?.role === 'agent' }

  async _load() {
    this._loading = true
    try {
      this._folders = await api.get('/kb/folders')
      if (this._isAgent) this._clients = await api.get('/clients').catch(() => [])
    } catch (err) { toast(err.message || 'Failed to load the knowledge base', 'error') }
    finally { this._loading = false }
  }

  _onSearch(e) {
    this._query = e.target.value
    clearTimeout(this._searchTimer)
    const q = this._query.trim()
    if (q.length < 2) { this._results = null; return }
    this._searchTimer = setTimeout(async () => {
      try { this._results = await api.get(`/kb/search?q=${encodeURIComponent(q)}`) }
      catch { this._results = [] }
    }, 250)
  }

  async _toggleFolder(id) {
    const next = new Set(this._expanded)
    if (next.has(id)) { next.delete(id); this._expanded = next; return }
    next.add(id); this._expanded = next
    if (!this._articlesByFolder[id]) {
      try {
        const folder = await api.get(`/kb/folders/${id}`)
        this._articlesByFolder = { ...this._articlesByFolder, [id]: folder.articles }
      } catch (err) { toast(err.message || 'Failed to load folder', 'error') }
    }
  }

  async _createFolder() {
    const name = this._fName.trim()
    if (!name) return
    this._busy = true
    try {
      await api.post('/kb/folders', {
        name,
        ...(this._fParent ? { parentId: this._fParent } : {}),
        ...(this._fClient ? { clientId: this._fClient } : {})
      })
      this._fName = ''; this._fClient = ''; this._fParent = ''; this._showFolderForm = false
      await this._load()
      toast('Folder created', 'success')
    } catch (err) { toast(err.message || 'Failed to create folder', 'error') } finally { this._busy = false }
  }

  async _deleteFolder(f) {
    if (!confirm(`Delete "${f.name}" and everything inside it — subfolders and articles? You can recover it later.`)) return
    try {
      await api.delete(`/kb/folders/${f.id}`)
      toast('Folder deleted', 'success')
      this._expanded = new Set()
      this._articlesByFolder = {}
      await this._load()
    } catch (err) { toast(err.message || 'Failed to delete folder', 'error') }
  }

  _childrenOf(parentId) { return this._folders.filter(f => (f.parentId || null) === parentId) }

  async _createArticle() {
    const editor = this.renderRoot.querySelector('rich-editor')
    if (!this._aFolder || !this._aTitle.trim() || !editor || editor.isEmpty()) {
      toast('Folder, title and body are required', 'info'); return
    }
    this._busy = true
    try {
      const { html: body } = editor.getContent()
      const art = await api.post('/kb/articles', { folderId: this._aFolder, title: this._aTitle.trim(), body })
      toast(`Article created`, 'success')
      navigate(`/kb/articles/${art.id}`)
    } catch (err) { toast(err.message || 'Failed to create article', 'error') } finally { this._busy = false }
  }

  _renderResults() {
    if (!this._results.length) return html`<div class="empty">No matches.</div>`
    return html`<div class="card">
      ${this._results.map(r => html`
        <div class="result" @click=${() => navigate(`/kb/articles/${r.id}`)}>
          <div class="t">${r.title}</div>
          <div class="f">${r.folderName}</div>
          <div class="s">${r.snippet ? renderSnippet(r.snippet) : ''}</div>
        </div>`)}
    </div>`
  }

  render() {
    if (this._loading) return html`<div class="loading">Loading…</div>`
    return html`
      <div class="head">
        <h1>Knowledge Base</h1>
        ${this._isAgent ? html`<span class="toolbar">
          <button @click=${() => (this._showFolderForm = !this._showFolderForm)}>New folder</button>
          <button class="primary" @click=${() => (this._showArticleForm = !this._showArticleForm)}>New article</button>
        </span>` : ''}
      </div>

      ${this._showFolderForm ? html`<div class="form">
        <input placeholder="Folder name" .value=${this._fName} @input=${e => (this._fName = e.target.value)} />
        <select .value=${this._fParent} @change=${e => (this._fParent = e.target.value)}>
          <option value="">Top-level folder</option>
          ${this._folders.map(f => html`<option value=${f.id}>Inside: ${f.name}</option>`)}
        </select>
        <select .value=${this._fClient} @change=${e => (this._fClient = e.target.value)}>
          <option value="">Public (all users)</option>
          ${this._clients.map(c => html`<option value=${c.id}>${c.name} (client-only)</option>`)}
        </select>
        <button class="primary" ?disabled=${this._busy || !this._fName.trim()} @click=${this._createFolder}>Create folder</button>
      </div>` : ''}

      ${this._showArticleForm ? html`<div class="form">
        <select .value=${this._aFolder} @change=${e => (this._aFolder = e.target.value)}>
          <option value="">Choose a folder…</option>
          ${this._folders.map(f => html`<option value=${f.id}>${f.name}${f.isPublic ? '' : ' (client-only)'}</option>`)}
        </select>
        <input placeholder="Article title" .value=${this._aTitle} @input=${e => (this._aTitle = e.target.value)} />
        <p class="hint">Use the toolbar to format; the video button embeds a YouTube or Vimeo link.</p>
        <rich-editor allowVideo .placeholder=${'Write the article…'}></rich-editor>
        <button class="primary" style="margin-top:0.6rem;" ?disabled=${this._busy} @click=${this._createArticle}>Create article</button>
      </div>` : ''}

      <input class="search" placeholder="Search the knowledge base…" .value=${this._query} @input=${this._onSearch} />

      ${this._results !== null
        ? this._renderResults()
        : this._folders.length
          ? html`<div class="card">${this._childrenOf(null).map(f => this._renderFolder(f, 0))}</div>`
          : html`<div class="empty">No knowledge base folders yet.</div>`}
    `
  }

  // Render one folder row + (when expanded) its articles, then its subfolders,
  // recursively — indented by depth so the hierarchy reads as a tree.
  _renderFolder(f, depth) {
    const open = this._expanded.has(f.id)
    return html`
      <div>
        <div class="folder" style="padding-left:${1 + depth * 1.25}rem">
          <span class="label" @click=${() => this._toggleFolder(f.id)}>
            <span class="caret">${open ? '▾' : '▸'}</span>
            <span class="fname">${f.name}</span>
            <span class="badge ${f.isPublic ? 'pub' : 'priv'}">${f.isPublic ? 'Public' : 'Client'}</span>
            <span class="count"> · ${f.articleCount} article${f.articleCount === 1 ? '' : 's'}</span>
          </span>
          ${this._isAgent ? html`<button class="del" @click=${() => this._deleteFolder(f)}>Delete</button>` : ''}
        </div>
        ${open ? html`<div class="articles" style="padding-left:${2 + depth * 1.25}rem">
          ${(this._articlesByFolder[f.id] || []).length
            ? this._articlesByFolder[f.id].map(a => html`<a @click=${() => navigate(`/kb/articles/${a.id}`)}>${a.title}</a>`)
            : html`<div class="none">No articles yet.</div>`}
        </div>` : ''}
        ${this._childrenOf(f.id).map(c => this._renderFolder(c, depth + 1))}
      </div>`
  }
}

// ts_headline emits <b>…</b> around matched terms. Render as escaped text (Lit
// escapes interpolated strings) with the matches wrapped in real <b> — so highlights
// show without treating the snippet as raw HTML.
function renderSnippet(snippet) {
  return snippet.split(/(<b>.*?<\/b>)/gs).filter(Boolean).map(part => {
    const m = part.match(/^<b>([\s\S]*)<\/b>$/)
    return m ? html`<b>${m[1]}</b>` : part
  })
}

customElements.define('page-kb', PageKb)
